/*
PURPOSE: Editorial shell for long hardcoded legal documents (Privacy,
Terms, ArtistTerms). Warm editorial chrome + a scoped prose typography
wrapper so legal markup is restyled WITHOUT re-tagging each element.
USE CASES:
- Privacy / Terms / ArtistTerms pages.
DOMAIN BOUNDARIES:
- Presentation only. `title` is the page heading; `children` is the legal
  body (raw h2/h3/p/ol/ul/li/a/b) reproduced verbatim by the page — this
  shell only themes it via descendant selectors (LEGAL_PROSE), so legal
  text is never re-tagged.
- No imports from design-sandbox/.
*/

import React from 'react';
import EditorialSection from './EditorialSection';
import DisplayHeading from './DisplayHeading';

// Descendant-selector theme — out-specifies any legacy element classes and
// lets the page move legal markup in unchanged.
export const LEGAL_PROSE =
  'max-w-prose text-editorial-body text-editorial-muted/80 leading-relaxed ' +
  '[&_h2]:font-cormorant [&_h2]:text-editorial-muted [&_h2]:text-3xl [&_h2]:md:text-4xl [&_h2]:leading-tight [&_h2]:mt-14 ' +
  '[&_h3]:font-cormorant [&_h3]:text-editorial-muted [&_h3]:text-2xl [&_h3]:md:text-3xl [&_h3]:leading-tight [&_h3]:mt-10 ' +
  '[&_p]:mt-4 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:list-inside [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:list-inside [&_li]:mt-1 ' +
  '[&_a]:text-editorial-accent [&_a]:underline [&_b]:text-editorial-muted [&_b]:font-semibold [&_strong]:text-editorial-muted [&_strong]:font-semibold';

interface EditorialLegalDocProps {
  title: string;
  children: React.ReactNode;
}

const EditorialLegalDoc: React.FC<EditorialLegalDocProps> = ({ title, children }) => {
  return (
    <div className="flex flex-col min-h-screen bg-ivory">
      <EditorialSection background="ivory" topRule={false}>
        <DisplayHeading as="h1" scale="display">
          {title}
        </DisplayHeading>
      </EditorialSection>
      <EditorialSection background="cream">
        <div className={LEGAL_PROSE}>{children}</div>
      </EditorialSection>
    </div>
  );
};

export default EditorialLegalDoc;
